import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "../../../components/select";
import { useDispatch, useSelector } from "react-redux";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { Link, useNavigate } from "react-router-dom";
import { useServerSnapshotsList } from "../layout/resuableEffect";
import { formatDateTime } from "../../../hooks/constants";
import Modal from "../../../components/modal";
import {
  deleteServerSnapshot,
  deleteVolumeImage,
  listServers,
  updateServerSnapshot,
} from "../../../hooks/local/userReducer";
import Button from "../../../components/button";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import usePartnerNavigation from "../../../hooks/usePartnerNavigation";



const ManageServerSnapshots = () => {


  document.title = "Server Snapshots - CloudVantage";

  const dispatch = useDispatch();
  const { navigate, generatePartnerUrl } = usePartnerNavigation();

  const projectId = useSelector((state) => state.user.selectedProject).id;

  const options = [
    { value: "all-snapshots", label: "All snapshots" },
    { value: "os-version", label: "OS Version" },
    { value: "os-distro", label: "OS Distro" },
    { value: "status", label: "Status" },
  ];

  const [searchText, setSearchText] = useState("");
  const [manageSnapshotModal, setManageSnapshotModal] = useState(false);
  const [editSnapshotModal, setEditSnapshotModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [isDeleteImageModal, setIsDeleteImageModal] = useState(false);

  const showManageSnapshotModal = (key, data) => {
    setManageSnapshotModal(key);
    const snapshotDetails = {
      snapshotId: data.id,
      snapshotName: data.name,
      snapshotSize: data.size,
    };
    sessionStorage.setItem(
      "selectedServerSnapshot",
      JSON.stringify(snapshotDetails)
    );
  };

  const handleLaunchSnapshot = (snapshot) => {
    const selectedSnapshot = {
      snapshotId: snapshot.id,
      snapshotName: snapshot.name,
      snapshotSize: snapshot.size || "0.0 GB",
      snapshotOS: snapshot?.osDistro,
      action: "launch",
    };

    sessionStorage.setItem(
      "selectedServerSnapshot",
      JSON.stringify(selectedSnapshot)
    );
    navigate("/client-area/vps");
  };

  const closeManageSnapshotModal = () => {
    setManageSnapshotModal(false);
  };

  const toggleEditSnapshotModal = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setEditSnapshotModal(!!snapshot);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const serverSnapshotList = useServerSnapshotsList();

  const snapshots = serverSnapshotList
    ? Object.values(serverSnapshotList).flat()
    : [];

  const snapshotListMap = Array.isArray(snapshots)
    ? snapshots.map((snapshot, index) => ({
        sn: index + 1,
        id: snapshot?.id,
        name: snapshot?.name,
        osDistro: snapshot?.os_distro,
        osVersion: snapshot?.os_version,
        size: snapshot?.size,
        status: snapshot?.status,
        createdAt: formatDateTime(snapshot?.created_at),
      }))
    : [];

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const filteredData = snapshotListMap?.filter(
    (row) =>
      row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      row?.status?.toLowerCase().includes(searchText.toLowerCase()) ||
      row?.osVersion?.toLowerCase().includes(searchText.toLowerCase()) ||
      row?.osDistro?.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleServerAction = async (action, payload) => {
    const { payload: response } = await dispatch(action(payload));
    if (response.statuscode === "00") {
      window.location.reload();
    }
  };

  const deleteSnapshot = (snapshotId) =>
    handleServerAction(deleteServerSnapshot, { projectId, snapshotId });

  const editSnapshot = useFormik({
    initialValues: {
      snapshotName: selectedSnapshot?.name || "",
      description: selectedSnapshot?.description || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      snapshotName: Yup.string().required("Snapshot name is required"),
    }),
    onSubmit: async (values) => {
      const { snapshotName, description } = values;
      const snapshotData = {
        snapshotId: selectedSnapshot.id,
        name: snapshotName,
        description,
        projectId,
      };

      await handleServerAction(updateServerSnapshot, snapshotData);
      toggleEditSnapshotModal(null);
    },
  });
  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">
        Manage Server Snapshots
      </p>

      {snapshotListMap?.length > 0 ? (
        <div>
          <div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
              <div className="md:col-span-3">
                <Select
                  label="Filter payments"
                  name="payments"
                  options={options}
                />
              </div>
              <div className="md:col-span-4">
                <input
                  type="search"
                  name=""
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by name, os distro or status..."
                  className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
              <table className="w-full text-sm text-left overflow-hidden">
                <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                  <tr>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      S/N
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      OS Distro
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      OS Version
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      Size
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-4 whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                  {currentEntries.map((row, index) => (
                    <tr
                      key={index}
                      className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                    >
                      <td className="text-start py-4 px-6">{row?.sn}.</td>
                      <td className="text-start py-4 px-6">{row?.name}</td>
                      <td className="text-start py-4 px-6">{row?.osDistro}</td>
                      <td className="text-start py-4 px-6">{row?.osVersion}</td>
                      <td className="text-start py-4 px-6">{row?.size}</td>
                      <td className="text-start py-4 px-6">{row?.status}</td>
                      <td className="text-start py-4 px-6">{row?.createdAt}</td>
                      <td className="text-start py-4 px-6">
                        <button
                          // disabled={true}
                          className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                          onClick={() => showManageSnapshotModal(index, row)}
                        >
                          Manage Snapshot
                        </button>

                        {manageSnapshotModal === index && (
                          <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                            <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                              <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                                <div className="flex gap-2 items-center">
                                  <span className="dark:text-white">
                                    Manage "{row.name}"
                                  </span>
                                </div>
                                <img
                                  src={close}
                                  onClick={closeManageSnapshotModal}
                                  className="h-5 cursor-pointer"
                                  alt=""
                                />
                              </div>
                              <div className="p-4 grid gap-1">
                                <Link
                                  to={generatePartnerUrl("/createVolumeFromSnapshot")}
                                  className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
                                >
                                  <span>
                                    Create volume from server snapshot
                                  </span>
                                  <span>
                                    <img src={arrow} alt="" className="" />
                                  </span>
                                </Link>

                                <div
                                  className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                  onClick={() => {
                                    closeManageSnapshotModal();
                                    toggleEditSnapshotModal(row);
                                  }}
                                >
                                  <span>Edit snapshot</span>
                                  <span>
                                    <img src={arrow} alt="" className="" />
                                  </span>
                                </div>

                                <div
                                  className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                  onClick={() => {
                                    handleLaunchSnapshot(row);
                                    closeManageSnapshotModal();
                                  }}
                                >
                                  <span>Launch</span>
                                  <span>
                                    <img src={arrow} alt="" className="" />
                                  </span>
                                </div>

                                <div
                                  className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                  onClick={() => {
                                    setIsDeleteImageModal(true);
                                  }}
                                >
                                  <span className="hover:underline">
                                    Delete Snapshot
                                  </span>
                                </div>
                              </div>
                              <Modal
                                title={"Delete Snapshot"}
                                isVisible={isDeleteImageModal}
                                onClose={() => setIsDeleteImageModal(false)}
                              >
                                <div className="text-sm text-center pt-6 text-wrap">
                                  Are you sure you want to delete{" "}
                                  <span className="underline">{row.name}</span>{" "}
                                  . This action cannot be undone.
                                </div>
                                <div className="grid gap-4 justify-center py-6">
                                  <button
                                    className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                    onClick={() => deleteSnapshot(row.id)}
                                  >
                                    Yes, delete {row.name}
                                  </button>
                                  <button
                                    className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                    onClick={() => setIsDeleteImageModal(false)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </Modal>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
              <p className="text-gray-400 text-sm">
                Showing{" "}
                <span className="font-medium text-black dark:text-white">
                  {indexOfFirstEntry + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-black dark:text-white">
                  {Math.min(indexOfLastEntry, snapshotListMap?.length)}
                </span>{" "}
                of{" "}
                <span className="font-medium text-black dark:text-white">
                  {snapshotListMap?.length}
                </span>{" "}
                results
              </p>
              <div className=" flex gap-3">
                <button
                  className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button
                  className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="md:px-52 text-center grid text-gray-500 mt-32">
            <div>
              It looks like you don't have a server snapshot yet. Once you make
              a snaphot for a server, your server snapshot details will appear
              here.
            </div>
            <Link
              to={generatePartnerUrl("/client-area/vps")}
              className="border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md p-3 flex items-center justify-self-center mt-8 w-fit text-xs"
            >
              <span>View VPS plans</span>
              <span className="ps-2">
                <img src={arrow} alt="" />
              </span>
            </Link>
          </div>
        </>
      )}

      {/* Edit Snapshot Modal */}
      {editSnapshotModal && selectedSnapshot && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">
                Edit Snapshot "{selectedSnapshot?.name}"
              </p>
              <span
                onClick={() => toggleEditSnapshotModal(null)}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            <form className="gap-y-6 grid" onSubmit={editSnapshot.handleSubmit}>
              <Input
                label={"Snapshot Name"}
                type={"text"}
                name={"snapshotName"}
                value={editSnapshot.values.snapshotName}
                onBlur={editSnapshot.handleBlur}
                onChange={editSnapshot.handleChange}
                onError={
                  editSnapshot.errors.snapshotName &&
                  editSnapshot.touched.snapshotName
                    ? editSnapshot.errors.snapshotName
                    : null
                }
              />
              <TextArea
                label={"Description"}
                type={"text"}
                name={"description"}
                value={editSnapshot.values.description}
                onBlur={editSnapshot.handleBlur}
                onChange={editSnapshot.handleChange}
                onError={
                  editSnapshot.errors.description &&
                  editSnapshot.touched.description
                    ? editSnapshot.errors.description
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button buttonText={"Update Snapshot"} buttonRole={"submit"} />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageServerSnapshots;
